import html2canvas from "html2canvas";
import apiClient from "api";

export type ExportFormat = "jpeg" | "pdf";

interface ExportOptions {
  filename: string;
  dashboardName: string;
}

function sanitizeFilename(name: string): string {
  return name.replace(/[^a-zA-Z0-9-_]/g, "_").replace(/_+/g, "_");
}

function generateFilename(dashboardName: string, extension: string): string {
  const timestamp = new Date().toISOString().split("T")[0];
  const sanitizedName = sanitizeFilename(dashboardName);
  return `report-${sanitizedName}-${timestamp}.${extension}`;
}

function triggerDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

async function exportAsJPEG(
  element: HTMLElement,
  dashboardName: string,
): Promise<void> {
  const originalScrollY = window.scrollY;
  const originalOverflow = document.body.style.overflow;

  const exportButtons = element.querySelectorAll(".widget-export-button");
  exportButtons.forEach((btn) => {
    (btn as HTMLElement).style.visibility = "hidden";
  });

  window.scrollTo(0, 0);
  document.body.style.overflow = "visible";

  await new Promise((resolve) => setTimeout(resolve, 500));

  const canvas = await html2canvas(element, {
    scale: 2,
    useCORS: true,
    allowTaint: true,
    logging: false,
    windowHeight: element.scrollHeight,
    height: element.scrollHeight,
  });

  window.scrollTo(0, originalScrollY);
  document.body.style.overflow = originalOverflow;

  exportButtons.forEach((btn) => {
    (btn as HTMLElement).style.visibility = "";
  });

  const blob = await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (b) => {
        if (b) {
          resolve(b);
        } else {
          reject(new Error("Failed to create blob from canvas"));
        }
      },
      "image/jpeg",
      0.95,
    );
  });

  const filename = generateFilename(dashboardName, "jpg");
  triggerDownload(blob, filename);
}

function exportAsPDF(_element: HTMLElement, _dashboardName: string): void {
  window.print();
}

export async function exportDashboard(
  format: ExportFormat,
  element: HTMLElement | null,
  dashboardName: string,
): Promise<void> {
  if (!element) {
    throw new Error("Export element not found");
  }

  if (format === "jpeg") {
    await exportAsJPEG(element, dashboardName);
  } else if (format === "pdf") {
    exportAsPDF(element, dashboardName);
  }
}

export const EXPORT_FORMATS: { value: ExportFormat; label: string }[] = [
  { value: "jpeg", label: "JPEG" },
  { value: "pdf", label: "PDF" },
];

export async function exportWidgetAsJPEG(
  element: HTMLElement,
  widgetTitle: string,
): Promise<void> {
  const exportButtons = element.querySelectorAll(".widget-export-button");
  exportButtons.forEach((btn) => {
    (btn as HTMLElement).style.visibility = "hidden";
  });

  const canvas = await html2canvas(element, {
    scale: 2,
    useCORS: true,
    allowTaint: true,
    logging: false,
    backgroundColor: null,
  });

  exportButtons.forEach((btn) => {
    (btn as HTMLElement).style.visibility = "";
  });

  const blob = await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (b) => {
        if (b) {
          resolve(b);
        } else {
          reject(new Error("Failed to create blob from canvas"));
        }
      },
      "image/jpeg",
      0.95,
    );
  });

  const timestamp = new Date().toISOString().split("T")[0];
  const sanitizedTitle = sanitizeFilename(widgetTitle);
  const filename = `widget-${sanitizedTitle}-${timestamp}.jpg`;
  triggerDownload(blob, filename);
}

export async function exportWidgetAsExcel(
  dashboardId: number,
  widgetId: string,
  widgetKey: string,
  widgetTitle: string,
): Promise<void> {
  const rawResponse = await apiClient.requestRaw(
    "/organization-member/export-widget-data",
    {
      method: "POST",
      data: {
        dashboardId,
        widgetId,
        widgetKey,
      },
    },
  );

  const blob = await rawResponse.blob();

  const timestamp = new Date().toISOString().split("T")[0];
  const sanitizedTitle = sanitizeFilename(widgetTitle);
  const filename = `widget-${sanitizedTitle}-${timestamp}.xlsx`;
  triggerDownload(blob, filename);
}
